/**
 * The bid workspace's API — ADR 0060 §5 shape, applied to bid writing.
 *
 * A bid is a `#UID-` folder on the Bid Office SharePoint, keyed by its
 * uid ("2026-113"). The rows, the generated documents and the folder are
 * shared across the team; the helper chat and uploads are the caller's
 * own conversation on the bid, which `getBid` finds or creates.
 */
import { acquireToken, USE_REAL_AUTH } from "../auth/msal";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export interface RequirementCounts {
  total: number;
  must: number;
  must_unowned: number;
  answered: number;
}
export interface ScoreCriterion {
  name: string;
  verdict: "ok" | "risk" | "stop" | "ukendt";
  evidence: string;
  source: string;
}
/** The assistant's decision basis, as its tool wrote it. */
export interface Recommendation {
  recommendation: "bid" | "nobid" | "uklart";
  summary: string;
  criteria: ScoreCriterion[];
  disqualifiers: string[];
  missing: string[];
  by: string;
  at: string;
}
export type Decision = "" | "bid" | "nobid" | "afventer";
export interface NextStep {
  key: string;
  label: string;
  tab: string;
  ask: string;
}
export interface Bid {
  uid: string;
  decision: Decision;
  decision_label: string;
  decision_note: string;
  decided_by_name: string;
  decided_at: string;
  recommendation_verdict: string;
  /** Full scorecard on the detail call only; null in the list. */
  recommendation: Recommendation | null;
  folder_name: string;
  framework: string;
  customer: string;
  title: string;
  deadline: string;
  deadline_text: string;
  deadline_time: string;
  stage: string;
  stage_label: string;
  phase: "aktiv" | "afleveret" | "fravalgt" | "arkiv";
  origin: "sharepoint" | "app";
  archived: boolean;
  doc_count: number;
  subfolders: string[];
  web_url: string;
  variants: string[];
  /** Other folders using this UID number for a different bid. */
  conflicts: string[];
  requirements: RequirementCounts;
  last_seen_at: string;
  updated_at: string;
}
export interface BidList {
  bids: Bid[];
  archived_count: number;
  rebuilt_at: string;
}
export interface BidStage {
  key: string;
  label: string;
  done: boolean;
}
export interface BidCounts extends RequirementCounts {
  must_open: number;
  unanswered: number;
  uploads: number;
  generated: number;
  documents: number;
}
export interface BidFile {
  name: string;
  path: string;
  url: string;
  source: string;
}
export interface BidFolder {
  name: string;
  files: BidFile[];
}
export interface GeneratedDoc {
  id: string;
  title: string;
  kind: string;
  format: string;
  folder: string;
  created_at: string;
  mine: boolean;
  download_url: string;
}
export interface BidUpload {
  id: string;
  name: string;
  rel_path: string;
  index_status: string;
}
export interface BidDetail {
  bid: Bid;
  conversation_id: number;
  stages: BidStage[];
  next_step: NextStep;
  counts: BidCounts;
  folders: BidFolder[];
  generated: GeneratedDoc[];
  uploads: BidUpload[];
}
export interface BidRequirement {
  id: number;
  seq: number;
  krav_id: string;
  text: string;
  req_type: string;
  must_should: string;
  source: string;
  answered_in: string;
  owner: string;
  status: string;
  note: string;
  origin: string;
  version: number;
  updated_by_name: string;
  updated_at: string;
}
export interface BidCreate {
  uid: string;
  title: string;
  customer: string;
  framework: string;
  deadline: string;
}

/** Thrown by `patchRequirement` when someone else wrote the row in between. */
export class RequirementConflict extends Error {
  current: BidRequirement;
  constructor(current: BidRequirement) {
    super("Rækken er ændret af en anden imens");
    this.current = current;
  }
}

async function headers(
  devUser: string,
  json = false,
): Promise<Record<string, string>> {
  const h: Record<string, string> = { Accept: "application/json" };
  if (json) h["Content-Type"] = "application/json";
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    if (t) h["Authorization"] = `Bearer ${t.token}`;
  } else if (devUser) {
    h["X-Dev-User"] = devUser;
  }
  return h;
}

async function ok<T>(r: Response, what: string): Promise<T> {
  if (!r.ok) {
    let detail = "";
    try {
      detail = ((await r.json()) as { detail?: string }).detail ?? "";
    } catch {
      /* body may not be JSON */
    }
    throw new Error(
      `${what} fejlede (${r.status})${detail ? `: ${detail}` : ""}`,
    );
  }
  return (await r.json()) as T;
}

export async function listBids(
  devUser: string,
  includeArchived = false,
): Promise<BidList> {
  const r = await fetch(
    `${API_BASE}/api/bids${includeArchived ? "?include_archived=true" : ""}`,
    { headers: await headers(devUser) },
  );
  return ok<BidList>(r, "hente bud");
}

export async function createBid(
  devUser: string,
  body: BidCreate,
): Promise<Bid> {
  const r = await fetch(`${API_BASE}/api/bids`, {
    method: "POST",
    headers: await headers(devUser, true),
    body: JSON.stringify(body),
  });
  return ok<Bid>(r, "oprette bud");
}

export async function setDecision(
  devUser: string,
  uid: string,
  decision: Decision,
  note: string,
): Promise<Bid> {
  const r = await fetch(
    `${API_BASE}/api/bids/${encodeURIComponent(uid)}/decision`,
    {
      method: "PATCH",
      headers: await headers(devUser, true),
      body: JSON.stringify({ decision, note }),
    },
  );
  return ok<Bid>(r, "gemme beslutning");
}

export async function getBid(devUser: string, uid: string): Promise<BidDetail> {
  const r = await fetch(`${API_BASE}/api/bids/${encodeURIComponent(uid)}`, {
    headers: await headers(devUser),
  });
  return ok<BidDetail>(r, "hente bud");
}

export async function listRequirements(
  devUser: string,
  uid: string,
): Promise<{ rows: BidRequirement[]; statuses: string[] }> {
  const r = await fetch(
    `${API_BASE}/api/bids/${encodeURIComponent(uid)}/requirements`,
    {
      headers: await headers(devUser),
    },
  );
  return ok(r, "hente krav");
}

export async function patchRequirement(
  devUser: string,
  uid: string,
  rowId: number,
  patch: Partial<
    Pick<BidRequirement, "owner" | "status" | "answered_in" | "note">
  >,
  version: number,
): Promise<BidRequirement> {
  const r = await fetch(
    `${API_BASE}/api/bids/${encodeURIComponent(uid)}/requirements/${rowId}`,
    {
      method: "PATCH",
      headers: await headers(devUser, true),
      body: JSON.stringify({ ...patch, version }),
    },
  );
  if (r.status === 409) {
    const body = (await r.json()) as { current: BidRequirement };
    throw new RequirementConflict(body.current);
  }
  return ok<BidRequirement>(r, "gemme ændring");
}

/** Upload one file into the caller's conversation on the bid, under a
 * folder path. Zips are unpacked server-side and keep their inner paths. */
export async function uploadToBid(
  devUser: string,
  conversationId: number,
  file: File,
  relativePath: string,
): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  form.append("relative_path", relativePath);
  const h = await headers(devUser);
  delete h["Accept"];
  const r = await fetch(
    `${API_BASE}/api/conversations/${conversationId}/uploads`,
    {
      method: "POST",
      headers: h,
      body: form,
    },
  );
  await ok(r, `uploade ${file.name}`);
}

/** Authenticated download to a local file — a plain <a href> cannot carry
 * the bearer. */
export async function downloadAuthed(
  devUser: string,
  path: string,
  filename: string,
): Promise<void> {
  const r = await fetch(`${API_BASE}${path}`, {
    headers: await headers(devUser),
  });
  if (!r.ok) throw new Error(`download fejlede (${r.status})`);
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
